import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient } from '@microsoft/signalr';
import {
  UserActivityTypeEnum,
  UserActivityTypeDescriptions,
  RouteToActivityTypeMap,
  CustomModalService,
  superAdminRoleStr,
  ClarityService,
} from '@piqet-angular/shared';
import Swal from 'sweetalert2';
import { CommonService } from 'libs/shared/src/lib/service/common.service';
import { SessionService } from 'libs/shared/src/lib/service/session.service';
import { filter } from 'rxjs';
import { NavigationEnd, Router, Event as RouterEvent } from '@angular/router';
import { AppUpdateService } from 'libs/shared/src/lib/service/AppUpdateServices/app-update.service';
import { JoyrideService } from 'ngx-joyride';
import { CookieHelperService } from '@piqet-angular/data';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
@Component({
  selector: 'piqet-angular-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private lastTrackedUrl: string | null = null;
  private readonly LAST_TRACKED_URL_KEY = 'lastTrackedUrl';
  userRole: any;
  superAdmin = superAdminRoleStr;
  constructor(
    private router: Router,
    private sessionService: SessionService,
    private commonService: CommonService,
    private model: CustomModalService,
    private AppUpdateServices: AppUpdateService,
    private joyrideService: JoyrideService,
    private cookieHelperService: CookieHelperService,
    private cookieService: CookieService,
    private sharedService: SharedService,
    private clarityService: ClarityService
  ) {
    this.initRouteTracking();
    this.AppUpdateServices.initializeUpdateService();
    this.clarityService.init(environment.clarityProjectId);
    window.addEventListener('piqet-force-logout', (event: any) => {
      this.sessionService.logout();
    });
  }

  ngOnInit(): void {
    this.applyThemeColors();
    // this.sessionService.initializeConnection(this.commonService.initDeviceId());
    const currentUrl = window.location.pathname.toLowerCase();
    this.lastTrackedUrl = localStorage.getItem(this.LAST_TRACKED_URL_KEY);
    if (
      currentUrl === '/purchaselicense/plicense'.toLowerCase() ||
      currentUrl === '/user/freetrial'.toLowerCase()
    ) {
      this.router.navigate(['/register']);
    }
  }
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.sessionService.onBeforeUnload();
  }

  ngOnDestroy(): void {
    localStorage.removeItem(this.LAST_TRACKED_URL_KEY);
    this.sessionService.stopConnection();
  }

  initRouteTracking(): void {
    this.router.events
      .pipe(filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        const baseRoute = event.urlAfterRedirects.split('?')[0].split('#')[0];

        const excludedRoutes = ['/login', '/forgot-password', '/register'];
        if (excludedRoutes.includes(baseRoute)) {
          return;
        }

        const isUserLoggedIn = !!this.cookieHelperService.getCookie('token');

        const userRole = this.cookieService.get('u_type');
        this.userRole = this.sharedService.decodeURIComponentParam(userRole)?.toLowerCase();
        if (
          !isUserLoggedIn ||
          this.userRole == this.superAdmin ||
          this.joyrideService.isTourInProgress()
        ) {
          return;
        }

        if (this.lastTrackedUrl !== baseRoute) {
          let activityType = this.getActivityTypeForRoute(baseRoute);

          if (!activityType) {
            activityType = UserActivityTypeEnum.Other;
          }

          this.logRouteActivity(activityType, baseRoute);
          this.lastTrackedUrl = baseRoute;
          localStorage.setItem(this.LAST_TRACKED_URL_KEY, baseRoute);
        }
      });
  }

  private getActivityTypeForRoute(url: string): UserActivityTypeEnum | null {
    const sortedRoutes = Object.keys(RouteToActivityTypeMap).sort((a, b) => b.length - a.length);

    for (const route of sortedRoutes) {
      const routeRegex = new RegExp('^' + route.replace('{id}', '[^/]+') + '$');

      if (routeRegex.test(url)) {
        return RouteToActivityTypeMap[route];
      }
      if (routeRegex.test(url.split('?')[0].split('#')[0])) {
        return RouteToActivityTypeMap[route];
      }
    }

    return null;
  }

  async logRouteActivity(activityType: UserActivityTypeEnum, url: string): Promise<void> {
    const payload = {
      activityType: activityType,
      ipAddress: '',
      additionalInfo: JSON.stringify({
        route: url,
      }),
      timestamp: new Date().toISOString(),
    };

    this.sessionService.logRouteActivities(payload).subscribe((res: any) => {
      if (res.messageType == 0) {
        if (res.data?.latestNews) {
          this.openAdditionalInfo(res.data?.latestNews);
        }
      }
    });
  }

  openAdditionalInfo(item: any) {
    if (this.joyrideService.isTourInProgress() || Swal.isVisible()) {
      return;
    }

    this.model.invoke('AdditionalInfoNews', {
      initialState: {
        item,
      },
      class: 'modal-xl',
    });
  }

  triggerLogout(): void {
    this.sessionService.logout();
  }

  private applyThemeColors(): void {
    const primary = environment.primaryColor;
    if (!primary) return;

    const root = document.documentElement;
    root.style.setProperty('--purple', primary);
    root.style.setProperty('--purple-opacity', primary + '1f');
    root.style.setProperty('--purple-hover', this.adjustColor(primary, -25));
    root.style.setProperty('--purple-light', this.adjustColor(primary, 60));
    root.style.setProperty(
      '--purple-gradient',
      `linear-gradient(135deg, ${primary} 0%, ${this.adjustColor(primary, -25)} 100%)`
    );
  }

  private adjustColor(hex: string, percent: number): string {
    const num = Number.parseInt(hex.replace('#', ''), 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;

    if (percent > 0) {
      r = Math.min(255, r + Math.round((255 - r) * (percent / 100)));
      g = Math.min(255, g + Math.round((255 - g) * (percent / 100)));
      b = Math.min(255, b + Math.round((255 - b) * (percent / 100)));
    } else {
      const factor = (100 + percent) / 100;
      r = Math.round(r * factor);
      g = Math.round(g * factor);
      b = Math.round(b * factor);
    }

    return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
  }
}
